import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import OTPField, { OTP_FIELD_LENGTH } from '../../../components/auth/otp-field';
import debugPromise from '../../../utils/debug-promise';
import FieldError from '../field-error';
import {
  formatCountdown,
  outlinedButtonSx,
  solidButtonSx,
} from '../styles';
import TooManyAttemptsModal from './too-many-attempts-modal';

export type CodeVerificationChannel = 'email' | 'sms';

export interface CodeVerificationViewProps {
  channel: CodeVerificationChannel;
  destination: string;
  isVerifying?: boolean;
  verificationError?: boolean;
  cooldownSeconds?: number;
  showAlternateChannel?: boolean;
  showTooManyAttempts?: boolean;
  onVerify: (code: string) => void | Promise<void>;
  onClearError: () => void;
  onResendCode: () => Promise<void>;
  onUseAlternateChannel: () => void | Promise<void>;
  onChangeEmail: () => void;
  onUsePasswordVerification: () => void;
}

const CHANNEL_COPY: Record<CodeVerificationChannel, {
  title: string;
  alternateChannelLabel: string;
  debugPrefix: string;
}> = {
  email: {
    title: 'Check Your Email',
    alternateChannelLabel: 'Get a Code by Text (SMS)',
    debugPrefix: 'emailVerificationView',
  },
  sms: {
    title: 'Check Your Phone',
    alternateChannelLabel: 'Get a Code by Email',
    debugPrefix: 'smsVerificationView',
  },
};

const CodeVerificationView = ({
  channel,
  destination,
  isVerifying = false,
  verificationError = false,
  cooldownSeconds = 0,
  showAlternateChannel = true,
  showTooManyAttempts = false,
  onVerify,
  onClearError,
  onResendCode,
  onUseAlternateChannel,
  onChangeEmail,
  onUsePasswordVerification,
}: CodeVerificationViewProps): React.ReactElement => {
  const [otpValue, setOtpValue] = useState('');
  const [otpKey, setOtpKey] = useState(0);
  const [isSwitchingChannel, setIsSwitchingChannel] = useState(false);
  const copy = CHANNEL_COPY[channel];

  const isCodeComplete = otpValue.length === OTP_FIELD_LENGTH;
  const showError = verificationError && !isVerifying && !showTooManyAttempts;
  const isCoolingDown = cooldownSeconds > 0;
  const actionsDisabled = isVerifying || isSwitchingChannel || showTooManyAttempts;

  useEffect(() => {
    if (!isVerifying && verificationError) {
      setOtpValue('');
      setOtpKey((prev) => prev + 1);
    }
  }, [isVerifying, verificationError]);

  const handleResend = () => {
    if (isCoolingDown || actionsDisabled) {
      return;
    }
    debugPromise(`${copy.debugPrefix}:resend.onResendCode`, onResendCode());
    setOtpValue('');
    setOtpKey((prev) => prev + 1);
    onClearError();
  };

  const handleUseAlternateChannel = () => {
    if (actionsDisabled) {
      return;
    }
    setIsSwitchingChannel(true);
    debugPromise(
      `${copy.debugPrefix}:onUseAlternateChannel`,
      Promise.resolve(onUseAlternateChannel()).finally(() => {
        setIsSwitchingChannel(false);
      }),
    );
  };

  const handleVerify = (code: string) => {
    if (actionsDisabled || code.length !== OTP_FIELD_LENGTH) {
      return;
    }
    debugPromise(`${copy.debugPrefix}:onVerify`, Promise.resolve(onVerify(code)));
  };

  const handleOtpChange = (code: string) => {
    if (verificationError) {
      onClearError();
    }
    setOtpValue(code);
  };

  const resendLink = (
    <Box
      component="button"
      type="button"
      onClick={handleResend}
      disabled={isVerifying}
      sx={{
        border: 0,
        background: 'none',
        p: 0,
        m: 0,
        cursor: 'pointer',
        font: 'inherit',
        color: 'primary.main',
        textDecoration: 'underline',
        '&:hover': { color: 'secondary.main' },
        '&:disabled': { cursor: 'default', opacity: 0.6 },
      }}
    >
      {channel === 'email' ? 'send a new code' : 'Send a New Code'}
    </Box>
  );

  const resendMessage = (() => {
    if (isVerifying) {
      return 'Checking your code…';
    }
    if (isCoolingDown) {
      if (channel === 'email') {
        return (
          <>
            Didn&apos;t get it? Check your spam or send a new code in
            {' '}
            {formatCountdown(cooldownSeconds)}
          </>
        );
      }
      return (
        <>
          Didn&apos;t get it? Send a new code in
          {' '}
          {formatCountdown(cooldownSeconds)}
        </>
      );
    }
    if (channel === 'email') {
      return (
        <>
          Didn&apos;t get it? Check your spam or
          {' '}
          {resendLink}
        </>
      );
    }
    return (
      <>
        Didn&apos;t get it?
        {' '}
        {resendLink}
      </>
    );
  })();

  return (
    <Box
      width="100%"
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} width="100%">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            aria-label="Change email"
            onClick={onChangeEmail}
            disabled={actionsDisabled}
            sx={{
              color: '#070707',
              ml: -1,
              alignSelf: 'flex-start',
            }}
          >
            <ChevronIcon style={{ transform: 'rotate(180deg)', width: 20, height: 20 }} />
          </IconButton>
        </Box>

        <Stack spacing={2} alignItems="center" width="100%">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Satoshi, sans-serif',
              fontWeight: 500,
              fontSize: '28px',
              lineHeight: '40px',
              letterSpacing: 0,
              textAlign: 'center',
              color: '#070707',
            }}
          >
            {copy.title}
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            We sent a code to
            {' '}
            <strong>{destination}</strong>
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2} alignItems="center" width="100%" sx={{ mt: '25px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <OTPField
            key={otpKey}
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleVerify}
            disabled={actionsDisabled}
            error={showError}
          />

          {showError && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <FieldError message="Code doesn't match. Try again." />
            </Box>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" textAlign="center">
          {resendMessage}
        </Typography>
      </Stack>

      <Stack spacing={2} width="100%" sx={{ mt: 'auto', pt: 4 }}>
        <LoadingButton
          variant="contained"
          fullWidth
          loading={isVerifying}
          disabled={!isCodeComplete || actionsDisabled}
          onClick={() => handleVerify(otpValue)}
          sx={solidButtonSx}
        >
          Sign in
        </LoadingButton>
        {showAlternateChannel && (
          <LoadingButton
            variant="outlined"
            fullWidth
            loading={isSwitchingChannel}
            disabled={actionsDisabled}
            onClick={handleUseAlternateChannel}
            sx={outlinedButtonSx}
          >
            {copy.alternateChannelLabel}
          </LoadingButton>
        )}
        <Button
          variant="outlined"
          fullWidth
          disabled={actionsDisabled}
          onClick={onUsePasswordVerification}
          sx={outlinedButtonSx}
        >
          Enter Your Password
        </Button>
      </Stack>

      <TooManyAttemptsModal
        open={showTooManyAttempts}
        onBackToSignIn={onChangeEmail}
      />
    </Box>
  );
};

export default CodeVerificationView;
